import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useWindowSize } from 'usehooks-ts';
import ReactFlow, {
  getRectOfNodes,
  getTransformForBounds,
  NodeMouseHandler,
  ReactFlowInstance,
} from 'react-flow-renderer';
import 'react-flow-renderer/dist/style.css';

import './index.less';

import { LAYOUT_WIDTH_BREAKPOINTS } from '../../../../../constants';

import {
  defaultEdgeOptions,
  FLOW_CHART_CLASS_NAME,
  FLOW_CHART_FIT_VIEW_OPTIONS,
  FLOW_CHART_LEFT_PADDING,
  FLOW_CHART_MAX_ZOOM,
  FLOW_CHART_MIN_ZOOM,
  FLOW_CHART_NO_INTERACTION_CLASS_NAME,
} from './const';
import { buildFlowNodes, flowEdges } from './flowGraph';
import { flowNodeTypes } from './nodes';
import { GuardrailsFlowNodeData, IGuardrailsFlowChartProps } from './types';

const GuardrailsFlowChart: React.FC<IGuardrailsFlowChartProps> = ({ masking, saving, stageSteps, onAddStep }) => {
  const { t } = useTranslation();
  const windowSize = useWindowSize();
  const isCompactFlowChart = windowSize.width <= LAYOUT_WIDTH_BREAKPOINTS.medium;
  const flowChartRef = useRef<HTMLDivElement>(null);
  const flowInstanceRef = useRef<ReactFlowInstance<GuardrailsFlowNodeData, unknown>>();
  const flowChartFitFrameRef = useRef<number>();

  const fitFlowChart = useCallback(() => {
    const instance = flowInstanceRef.current;
    const chartElement = flowChartRef.current;

    if (!instance || !chartElement) {
      return;
    }

    const visibleNodes = instance.getNodes().filter((node) => !node.hidden);
    const nodesInitialized = visibleNodes.every((node) => node.width && node.height);

    if (!visibleNodes.length || !nodesInitialized) {
      if (flowChartFitFrameRef.current) {
        window.cancelAnimationFrame(flowChartFitFrameRef.current);
      }

      flowChartFitFrameRef.current = window.requestAnimationFrame(() => {
        flowChartFitFrameRef.current = undefined;
        fitFlowChart();
      });

      return;
    }

    const bounds = getRectOfNodes(visibleNodes);
    const [centeredX, y, zoom] = getTransformForBounds(
      bounds,
      chartElement.clientWidth,
      chartElement.clientHeight,
      FLOW_CHART_FIT_VIEW_OPTIONS.minZoom,
      FLOW_CHART_FIT_VIEW_OPTIONS.maxZoom,
      FLOW_CHART_FIT_VIEW_OPTIONS.padding
    );
    const leftAlignedX = FLOW_CHART_LEFT_PADDING - bounds.x * zoom;

    instance.setViewport({ x: Math.min(centeredX, leftAlignedX), y, zoom });
  }, []);

  const onFlowInit = useCallback(
    (instance: ReactFlowInstance<GuardrailsFlowNodeData, unknown>) => {
      flowInstanceRef.current = instance;
      fitFlowChart();
    },
    [fitFlowChart]
  );

  const onFlowNodeClick = useCallback<NodeMouseHandler>(() => undefined, []);

  useEffect(() => {
    const chartElement = flowChartRef.current;

    if (!chartElement || typeof ResizeObserver === 'undefined') {
      return;
    }

    const resizeObserver = new ResizeObserver(() => fitFlowChart());
    resizeObserver.observe(chartElement);

    return () => resizeObserver.disconnect();
  }, [fitFlowChart]);

  useEffect(
    () => () => {
      if (flowChartFitFrameRef.current) {
        window.cancelAnimationFrame(flowChartFitFrameRef.current);
      }
    },
    []
  );

  const flowNodes = useMemo(
    () =>
      buildFlowNodes({
        isCompactFlowChart,
        masking,
        onAddStep,
        saving,
        stageSteps,
      }),
    [isCompactFlowChart, masking, onAddStep, saving, stageSteps, t]
  );

  useEffect(() => {
    const fitTimeout = window.setTimeout(fitFlowChart, 0);

    return () => window.clearTimeout(fitTimeout);
  }, [fitFlowChart, flowNodes]);

  return (
    <div ref={flowChartRef} className={FLOW_CHART_CLASS_NAME}>
      <ReactFlow
        connectOnClick={false}
        defaultEdgeOptions={defaultEdgeOptions}
        edges={flowEdges}
        elementsSelectable={false}
        maxZoom={FLOW_CHART_MAX_ZOOM}
        minZoom={FLOW_CHART_MIN_ZOOM}
        nodes={flowNodes}
        nodesConnectable={false}
        nodesDraggable={false}
        nodeTypes={flowNodeTypes}
        noDragClassName={FLOW_CHART_NO_INTERACTION_CLASS_NAME}
        noPanClassName={FLOW_CHART_NO_INTERACTION_CLASS_NAME}
        noWheelClassName={FLOW_CHART_NO_INTERACTION_CLASS_NAME}
        panOnDrag={false}
        preventScrolling={false}
        proOptions={{ hideAttribution: true, account: 'paid-custom' }}
        selectNodesOnDrag={false}
        zoomOnDoubleClick={false}
        zoomOnPinch={false}
        zoomOnScroll={false}
        onInit={onFlowInit}
        onNodeClick={onFlowNodeClick}
      />
    </div>
  );
};

export default GuardrailsFlowChart;
